import { FocusEvent, KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Form, IconButton, Text } from '../Ant';
import { ChevronDown, Search } from '../icons';
import { usePopupFocus } from '../../hooks/usePopupFocus';
import { findNextField } from '../../helpers/fieldFocusHelper';
import LookupClearButton from '../lookupClearButton/lookupClearButton';
import SearchableSelectPanel, { SearchableSelectPanelProps } from './searchableSelectPanel';
import { Trigger, TriggerIcons } from './searchableSelectStyles';
import { SearchableSelectProps, SelectOption } from './searchableSelectTypes';

/** The portal still inherits the field's Form.Item status; a bare Form resets it so the search isn't painted as invalid. */
function renderPopup<V extends string | number>(panelProps: SearchableSelectPanelProps<V>) {
  return (
    <Form component={false}>
      <SearchableSelectPanel {...panelProps} />
    </Form>
  );
}

function SearchableSelect<V extends string | number = string>({
  options,
  value,
  onChange,
  placeholder,
  ariaLabel,
  emptyText,
  filters,
  onSearch,
  searchPlaceholder,
  onLoadMore,
  hasMore = false,
  loading = false,
  loadingMore = false,
  disabled = false,
  id,
  className,
  onSearchIconClick,
  onClear,
  'aria-describedby': ariaDescribedBy,
  'data-field': dataField,
  'data-testid': testId,
}: Readonly<SearchableSelectProps<V>>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const panelId = useId();

  const canClearValue = onClear !== undefined && !disabled && Boolean(value);

  const close = () => setOpen(false);
  const { contentRef, onKeyDown, onBlur, tabIndex } = usePopupFocus<HTMLDivElement>(open, close);

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowDown' || open) return;
    event.preventDefault();
    setOpen(true);
  };

  /** Moving focus to the trigger is a click on it; closing here as well would make that click reopen the panel. */
  const handlePanelBlur = (event: FocusEvent<HTMLElement>) => {
    if (event.relatedTarget === triggerRef.current) return;
    onBlur(event);
  };

  /** A value picked here moves on to the next field, as one picked on a picker screen does. */
  const moveToNextField = () => {
    const trigger = triggerRef.current;
    if (!trigger?.closest('.ant-form-item')) return;
    findNextField(trigger)?.focus();
  };

  const handleSelect = (option: SelectOption<V>) => {
    onChange(option);
    close();
    moveToNextField();
  };

  const accessibleName = value
    ? [`${ariaLabel}: ${value.label}`, value.description].filter(Boolean).join(', ')
    : ariaLabel;

  return (
    <>
      <Dropdown
        open={open}
        disabled={disabled}
        trigger={['click']}
        placement="bottomLeft"
        destroyOnHidden
        getPopupContainer={() => document.body}
        onOpenChange={setOpen}
        popupRender={() =>
          renderPopup({
            ref: contentRef,
            rootProps: { onKeyDown, onBlur: handlePanelBlur, tabIndex },
            panelId,
            options,
            selectedValue: value?.value,
            onSelect: handleSelect,
            ariaLabel,
            emptyText,
            filters,
            onSearch,
            searchPlaceholder,
            onLoadMore,
            hasMore,
            loading,
            loadingMore,
          })
        }
      >
        <Trigger
          ref={triggerRef}
          id={id}
          type="default"
          block
          disabled={disabled}
          className={['add-transaction-btn', 'white-space-wrap', className]
            .filter(Boolean)
            .join(' ')}
          aria-label={accessibleName}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          aria-describedby={ariaDescribedBy}
          data-field={dataField}
          data-testid={testId}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className="d-flex w-full items-center justify-between gap-10">
            {value ? (
              <div className="selected-values d-flex flex-col items-start min-w-0">
                <Text
                  $fontsize="13px"
                  $weight={value.description ? 500 : 400}
                  $color="title"
                  $lineheight="20px"
                  className="ta-left lineclamp-1"
                >
                  {value.label}
                </Text>
                {value.description && (
                  <Text
                    $fontsize="13px"
                    $weight={400}
                    $color="subtitle"
                    $lineheight="20px"
                    className="ta-left lineclamp-1"
                  >
                    {value.description}
                  </Text>
                )}
              </div>
            ) : (
              <div className="preselection-field d-flex min-w-0">
                <span className="lineclamp-1">{placeholder}</span>
              </div>
            )}
            <TriggerIcons aria-hidden="true">
              <ChevronDown className="select-chevron" />
              {onSearch && !onSearchIconClick && (
                <span data-search-icon data-testid={testId && `${testId}-search-icon`}>
                  <Search />
                </span>
              )}
            </TriggerIcons>
          </span>
        </Trigger>
      </Dropdown>
      {onSearch && onSearchIconClick && (
        <IconButton
          aria-label={`${ariaLabel}: ${t('common:search')}`}
          className="lookup-search-btn"
          disabled={disabled}
          icon={<Search />}
          data-testid={testId && `${testId}-search-icon`}
          onClick={onSearchIconClick}
        />
      )}
      {canClearValue && (
        <LookupClearButton data-testid={testId && `${testId}-clear-icon`} onClick={onClear} />
      )}
    </>
  );
}

export default SearchableSelect;
